import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { errorText } from "@/lib/calendar";

const COPY = {
  suspend: {
    title: "Suspend this account?",
    body: "They can't sign in and any open sessions end right away. Nothing is deleted, and you can restore the account at any time.",
    confirm: "Suspend account", tone: "bg-red-600 hover:bg-red-700",
  },
  restore: {
    title: "Restore this account?",
    body: "They can sign in again with their existing password.",
    confirm: "Restore account", tone: "bg-indigo-900 hover:bg-indigo-800",
  },
  reset: {
    title: "Email a password reset link?",
    body: "A single-use link that expires in 30 minutes goes only to the account's own email. You won't see the link, and the action is recorded in your activity log.",
    confirm: "Email reset link", tone: "bg-indigo-900 hover:bg-indigo-800",
  },
};

const PATHS = { suspend: "suspend", restore: "restore", reset: "send-reset-link" };

export const AccountActionDialog = ({ action, account, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const copy = COPY[action] || COPY.restore;

  const close = () => { setReason(""); onClose(); };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/owner/accounts/${account.id}/${PATHS[action]}`, action === "suspend" ? { reason } : undefined);
      toast.success(action === "reset" ? `Reset link emailed to ${data.sent_to}` : action === "suspend" ? "Account suspended" : "Account restored");
      setReason("");
      onDone();
    } catch (e) {
      toast.error(errorText(e, "That didn't work. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(action && account)} onOpenChange={(open) => !open && close()}>
      <DialogContent data-testid="owner-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{copy.title}</DialogTitle>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>
        {account && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm" data-testid="owner-action-target">
            <p className="font-semibold text-slate-900">{account.name}</p>
            <p className="text-slate-500">{account.email}</p>
          </div>
        )}
        {action === "suspend" && (
          <div className="space-y-2">
            <Label htmlFor="suspend-reason">Reason (kept in your activity log)</Label>
            <Textarea id="suspend-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
              placeholder="e.g. Old test login, not needed" data-testid="owner-suspend-reason-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="owner-action-cancel">Cancel</Button>
          <Button className={`${copy.tone} text-white`} onClick={run} data-testid="owner-action-confirm"
            disabled={busy || (action === "suspend" && reason.trim().length < 3)}>
            {busy ? "Working…" : copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
